import { useTranslation } from 'react-i18next';
import { useCapabilities } from '../hooks/useCapabilities';
import { useCallback, useEffect, useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    Box,
    Button,
    Grid2,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import PaymentBlock from '../pages/bookings/components/payment/PaymentBlock';
import { formatCurrency } from '../pages/bookings/helpers/formatters';
import { payReservation } from '@openapi/reservations/reservations';
import useNotify from '../hooks/useNotify';
import { calculateProcessingFee } from '../helpers/calculateProcessingFee';
import PaymentMethodList from '../pages/bookings/components/payment/PaymentMethodList';

function PaymentModal({
    title,
    content,
    order,
    allOrders,
    paymentMethods,
    reservationId,
    reservationHash,
    reloadData,
    venueAllowSplitPayment,
    reloadBookingData,
    absorbProcessingFee,
    showModal,
    MODAL_TYPE,
}) {
    const { t } = useTranslation();
    const theme = useTheme();
    // Phones can't fit the windowed dialog; go full-screen below the sm breakpoint.
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { can, CAPABILITIES } = useCapabilities();
    const canOverrideSplitLimit = can(CAPABILITIES.PAYMENTS_SPLIT_UNLIMITED);
    const { notifyError, notifySuccess } = useNotify();
    const closeAction = () => showModal(MODAL_TYPE.NONE);
    const [amount, setAmount] = useState(() => {
        // set the remaining amount to pay
        const finishedSum = order?.transactions.reduce(
            (acc, trx) => (trx.finished ? acc + trx.amount : 0),
            0,
        );
        return order.amount - finishedSum;
    });

    const [pickedPaymentMethod, setPickedPaymentMethod] = useState(() => {
        if (paymentMethods.length === 1) {
            return paymentMethods[0];
        }
        return null;
    });

    // on order change, recalculate the amount
    useEffect(() => {
        const finishedSum = order?.transactions.reduce(
            (acc, trx) => (trx.finished ? acc + trx.amount : 0),
            0,
        );
        setAmount(order.amount - finishedSum);
    }, [order, setAmount]);

    const hasMorePaymentMethods = paymentMethods.length > 1;
    const maxAmount =
        order.amount -
        order?.transactions.reduce((acc, trx) => (trx.finished ? acc + trx.amount : 0), 0);

    // unlimited when: venue setting is 0, OR agent has PAYMENTS_SPLIT_UNLIMITED capability
    const effectivelyUnlimited = venueAllowSplitPayment === 0 || canOverrideSplitLimit;
    const finishedTransactions = order.transactions.filter(
        (trx) => trx.finished && trx.paymentType !== 'coupon',
    ).length;
    // Net rate is charged in one payment (the full net); never offer split for it.
    const isNetRate =
        pickedPaymentMethod?.methodType === 'net_rate' ||
        pickedPaymentMethod?.methodType === 'net_rate_company';
    const showSplitPayment =
        !isNetRate && (effectivelyUnlimited || venueAllowSplitPayment > finishedTransactions + 1);
    const splitPaymentInfo = effectivelyUnlimited
        ? t('payment.unlimitedSplitPayments')
        : venueAllowSplitPayment > 1
          ? t('payment.paymentXofY', {
                current: finishedTransactions + 1,
                max: venueAllowSplitPayment,
            })
          : null;

    // Calculate processing fee based on amount and selected payment method.
    // When the show absorbs the card fee, Stripe card payments charge no
    // surcharge (matches the backend, which forces 0 and rejects a mismatch).
    // Net rate is a B2B company-card charge, also with no surcharge.
    const absorbsStripeFee = absorbProcessingFee && pickedPaymentMethod?.methodType === 'stripe';
    // Net rate charges exactly the net with no surcharge (backend forces 0).
    const processingFee =
        pickedPaymentMethod && !absorbsStripeFee && !isNetRate
            ? calculateProcessingFee(amount, pickedPaymentMethod)
            : 0;

    const startTransaction = useCallback(
        async (paymentInfo) => {
            if (!pickedPaymentMethod) {
                console.error('No payment method selected');
                return;
            }
            // The net_rate_company block passes the chosen company card here; it is a
            // top-level pay field, not transaction info, so pull it out.
            const { companyCardPaymentMethodId, ...transactionInfo } = paymentInfo || {};
            try {
                await payReservation(
                    reservationId,
                    {
                        orderId: order.id,
                        amount: maxAmount !== amount ? amount : null,
                        // method
                        paymentMethodSlug: pickedPaymentMethod?.slug,
                        processingFee: processingFee,
                        transactionInfo,
                        ...(companyCardPaymentMethodId ? { companyCardPaymentMethodId } : {}),
                    },
                    {
                        reservationHash,
                    },
                );
                reloadData();
                reloadBookingData();
                setPickedPaymentMethod(null);
                notifySuccess(t('payment.paymentStarted'));
                showModal(MODAL_TYPE.NONE);
            } catch (error) {
                console.error('Error starting transaction:', error);
                notifyError(t('errors.transactionFailed'));
            }
        },
        [
            pickedPaymentMethod,
            processingFee,
            reservationId,
            reservationHash,
            reloadData,
            reloadBookingData,
            notifySuccess,
            notifyError,
            t,
            maxAmount,
            amount,
            order.id,
            showModal,
            MODAL_TYPE,
        ],
    );

    return (
        <Dialog
            open
            fullWidth
            maxWidth="md"
            fullScreen={fullScreen}
            onClose={closeAction}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">{title}</DialogTitle>
            <DialogContent>
                {content}
                <Box mt={2} sx={{ width: '100%', maxWidth: 800, mx: 'auto' }}>
                    <Grid2 container alignItems="center" mb={1}>
                        <Grid2 size={{ xs: 12 }}>
                            <Typography variant="subtitle1" style={{ textAlign: 'left' }}>
                                {t('common.remaining')}: {formatCurrency(maxAmount / 100)}
                            </Typography>
                            {splitPaymentInfo && (
                                <Typography variant="body2" color="text.secondary">
                                    {splitPaymentInfo}
                                </Typography>
                            )}
                        </Grid2>
                    </Grid2>
                    {pickedPaymentMethod && !absorbsStripeFee && (
                        <Grid2 container alignItems="center" mb={1}>
                            <Grid2 size={{ xs: 12 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('transactions.columns.processing_fee')}:{' '}
                                    {formatCurrency(processingFee / 100)}
                                </Typography>
                                <Typography variant="subtitle1" style={{ textAlign: 'left' }}>
                                    <strong>
                                        {t('transactions.columns.total')}:{' '}
                                        {formatCurrency((amount + processingFee) / 100)}
                                    </strong>
                                </Typography>
                            </Grid2>
                        </Grid2>
                    )}
                    <Grid2 container alignItems="center" mb={1}>
                        <Grid2 size={{ xs: 12 }}>
                            <Typography variant="subtitle2" style={{ textAlign: 'left' }}>
                                {t('common.willPayInThisTransaction')}:
                            </Typography>
                        </Grid2>
                    </Grid2>
                    <Grid2 container spacing={2} alignItems="center" mb={2}>
                        <Grid2 size={{ xs: 6 }}>
                            <Box display="flex" alignItems="center">
                                <Typography>$</Typography>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={amount / 100}
                                    style={{ marginLeft: 8, width: 80 }}
                                    onChange={(e) =>
                                        setAmount(
                                            Math.max(
                                                0,
                                                Math.min(
                                                    maxAmount,
                                                    parseFloat(e.target.value) * 100,
                                                ),
                                            ),
                                        )
                                    }
                                    disabled={!showSplitPayment}
                                />
                            </Box>
                        </Grid2>
                        <Grid2 size={{ xs: 6 }}>
                            {showSplitPayment && (
                                <input
                                    type="range"
                                    min="0"
                                    max={maxAmount}
                                    value={amount}
                                    style={{ width: '100%' }}
                                    onChange={(e) => setAmount(parseInt(e.target.value))}
                                />
                            )}
                        </Grid2>
                    </Grid2>
                    {hasMorePaymentMethods && pickedPaymentMethod === null && paymentMethods && (
                        <Box
                            mb={2}
                            p={2}
                            border={1}
                            borderColor="grey.300"
                            borderRadius={1}
                            bgcolor="grey.50"
                        >
                            <Grid2 size={{ xs: 12 }} mb={2}>
                                <Typography variant="h6">{t('paymentmethods.title')}</Typography>
                            </Grid2>
                            <PaymentMethodList
                                paymentMethods={paymentMethods}
                                onPick={setPickedPaymentMethod}
                                t={t}
                            />
                        </Box>
                    )}
                    {pickedPaymentMethod && (
                        <Box
                            mt={2}
                            p={2}
                            border={1}
                            borderColor="grey.300"
                            borderRadius={1}
                            bgcolor="grey.50"
                        >
                            <PaymentBlock
                                order={order}
                                allOrders={allOrders}
                                paymentMethod={pickedPaymentMethod}
                                onStartTransaction={startTransaction}
                                onCancel={
                                    hasMorePaymentMethods
                                        ? () => setPickedPaymentMethod(null)
                                        : null
                                }
                                amount={amount}
                                maxAmount={maxAmount}
                            />
                        </Box>
                    )}
                </Box>
                <DialogActions>
                    <Button onClick={closeAction}>{t('common.close')}</Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
}
export default PaymentModal;
